import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useQuery } from "@tanstack/solid-query";
import { useSearch } from "@tanstack/solid-router";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { Button, buttonVariants } from "@/components/ui/button";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { api, SERVER_BASE_URL } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { handleApiResponse, queryKeys, sharedApi } from "@custodia/shared";
import { showToast } from "@/components/ui/toast";
import { Link2, ShieldCheck } from "@fleet/icons";

type ProviderId = "google" | "apple";

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: "Google",
  apple: "Apple",
};

const PROVIDERS_STALE_TIME_MS = 5 * 60 * 1000;

const linkUrl = (provider: ProviderId) =>
  `${SERVER_BASE_URL.replace(/\/$/u, "")}/api/auth/link-social/${provider}`;

/**
 * "Connected accounts": lets you link/unlink Google and Apple from the profile.
 * It only shows providers ENABLED in the backend. It respects the last-method
 * safeguard (the backend rejects leaving the user without access).
 */
export function ConnectedAccountsSection() {
  const { t } = useI18n();
  // Read ?linked=<provider> to show feedback after returning from the callback.
  const search = useSearch({ strict: false }) as () => { linked?: string };
  const [confirmUnlink, setConfirmUnlink] = createSignal<ProviderId | null>(null);
  const [busy, setBusy] = createSignal(false);

  // P23/CC-60 · phase 2 — the `isSuccess` guard from I2 was NOT enough, by a long shot.
  //
  // `openapi-fetch` NEVER throws: it returns `{data, error}`. With `return data ?? {…}`
  // a 503 resolved in **SUCCESS with a FABRICATED object**, so `isSuccess`
  // was `true`, `isError` was NEVER reached and the error box below was
  // dead code: a fix written against the wrong state.
  // `handleApiResponse` forces the throw with the body message, which is the
  // only thing that puts these queries in ERROR.
  const providersQuery = useQuery(() => ({
    queryKey: queryKeys.auth.socialProviders(),
    queryFn: async () => handleApiResponse(sharedApi.socialProviders(api)),
    staleTime: PROVIDERS_STALE_TIME_MS,
    retry: false,
  }));

  const methodsQuery = useQuery(() => ({
    queryKey: queryKeys.auth.loginMethods(),
    queryFn: async () => handleApiResponse(sharedApi.loginMethods(api)),
    retry: false,
  }));

  /**
   * Did EITHER of the two loads fail? `providers` matters as much as
   * `login-methods`: if it fails, `enabledProviders()` stays empty and the `<Show>`
   * below HIDES the entire section — showing neither error nor content. The symptom
   * is not "missing message", it is "nothing appears", leaving the error branch
   * unreachable through another door.
   */
  const hasLoadError = () => providersQuery.isError || methodsQuery.isError;

  const enabledProviders = createMemo<ProviderId[]>(() => {
    // P23 / CC-60 · I2: `createMemo` is eager → reads `.data` on mount.
    if (!providersQuery.isSuccess) {
      return [];
    }
    const p = providersQuery.data;
    if (!p) {
      return [];
    }
    const list: ProviderId[] = [];
    if (p.google) {
      list.push("google");
    }
    if (p.apple) {
      list.push("apple");
    }
    return list;
  });

  // P23 / CC-60 · I2. Without `isSuccess` guard, with `login-methods` down,
  // `?? []` turned "unknown" into "none linked" and the screen showed
  // "Not connected" for a Google account that WAS connected — with a "Connect"
  // button next to it. Even worse: `hasPassword` dropped to `false`, which
  // decides whether the LAST sign-in method can be unlinked.
  const linkedProviders = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.providers ?? []) : [],
  );
  const hasPassword = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.has_password ?? false) : false,
  );

  const isLinked = (provider: ProviderId) => linkedProviders().includes(provider);

  // Total number of access methods (password + each linked social account).
  const totalMethods = createMemo(() => linkedProviders().length);

  // Is this the LAST method? (it cannot be unlinked without establishing another).
  const isLastMethod = (provider: ProviderId) =>
    isLinked(provider) && totalMethods() <= 1 && !hasPassword();

  const handleUnlink = async (provider: ProviderId) => {
    setBusy(true);
    try {
      // CC-30: openapi-fetch does NOT throw on 4xx/5xx. `handleApiResponse` forces
      // throw (with body message) so 409/500 never passes as silent success;
      // catch also covers network errors (which do throw).
      await handleApiResponse(sharedApi.unlinkAccount(api, provider));
      showToast(t(msg`Account unlinked.`), "success");
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message.includes("only sign-in method")) {
        showToast(t(msg`Set a password before unlinking your only sign-in method.`), "error");
      } else {
        showToast(t(msg`Could not unlink the account. Please try again.`), "error");
      }
    } finally {
      setBusy(false);
      setConfirmUnlink(null);
    }
  };

  // Feedback when returning from the linking flow (?linked=<provider>).
  let linkedFeedbackDone = false;
  createEffect(() => {
    const provider = search().linked;
    if (provider && !linkedFeedbackDone) {
      linkedFeedbackDone = true;
      showToast(t(msg`Account connected.`), "success");
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    }
  });

  return (
    // Corollary of fix: section must show ALSO when loading failed.
    // With only `enabled > 0`, a failure in `providers` hid it entirely.
    <Show when={enabledProviders().length > 0 || hasLoadError()}>
      <Card>
        <CardHeader class="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Connected accounts`)}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3">
          <Show when={hasLoadError()}>
            <QueryErrorState
              testId="connected-accounts-error"
              compact
              description={t(
                msg`We couldn't reach the server, so we can't tell which accounts are connected.`,
              )}
              onRetry={() => {
                void providersQuery.refetch();
                void methodsQuery.refetch();
              }}
            />
          </Show>
          {/*
            It is WRAPPED, not chained: on error `isPending` is `false`, so a
            `when={!isPending && !isError}` with `fallback={skeleton}` would render
            the error box AND the skeleton at the same time, forever.
*/}
          <Show when={!hasLoadError()}>
            <Show
              when={!methodsQuery.isPending}
              fallback={
                <div class="space-y-3">
                  <Skeleton class="h-14 w-full rounded-xl" />
                  <Skeleton class="h-14 w-full rounded-xl" />
                </div>
              }
            >
              <For each={enabledProviders()}>
                {(provider) => (
                  <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">
                    <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Link2 class="h-5 w-5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-semibold">{PROVIDER_LABELS[provider]}</p>
                      <Show
                        when={isLinked(provider)}
                        fallback={
                          <p class="text-xs text-muted-foreground">{t(msg`Not connected`)}</p>
                        }
                      >
                        <Badge
                          variant="secondary"
                          class="mt-1 h-5 border-none bg-primary/20 px-1.5 text-[10px] font-bold uppercase tracking-wider text-primary"
                        >
                          {t(msg`Connected`)}
                        </Badge>
                      </Show>
                    </div>

                    <Show
                      when={isLinked(provider)}
                      fallback={
                        <a
                          href={linkUrl(provider)}
                          class={buttonVariants({ variant: "outline", size: "sm" })}
                          data-testid={`link-${provider}`}
                        >
                          {t(msg`Connect`)}
                        </a>
                      }
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy() || isLastMethod(provider)}
                        title={
                          isLastMethod(provider)
                            ? t(msg`Set a password before unlinking your only sign-in method.`)
                            : undefined
                        }
                        onClick={() => setConfirmUnlink(provider)}
                        class="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        {t(msg`Disconnect`)}
                      </Button>
                    </Show>
                  </div>
                )}
              </For>

              <Show when={!hasPassword() && totalMethods() <= 1}>
                <p class="text-xs text-muted-foreground">
                  {t(
                    msg`This is your only sign-in method. Set a password to be able to disconnect it.`,
                  )}
                </p>
              </Show>
            </Show>
          </Show>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUnlink(null);
          }
        }}
        title={t(msg`Disconnect account?`)}
        description={t(
          msg`You will no longer be able to sign in with this provider. You can reconnect it anytime.`,
        )}
        confirmLabel={t(msg`Disconnect`)}
        cancelLabel={t(msg`Cancel`)}
        onConfirm={() => {
          const provider = confirmUnlink();
          if (provider) {
            void handleUnlink(provider);
          }
        }}
      />
    </Show>
  );
}
